import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import DisplayAd from 'src/common/components/ads/DisplayAd';
import { BOTTOM_BANNER_ENABLED, FREESTAR_ENABLED } from 'src/common/constants/experiments/ads';
import useExperiment from 'src/common/hooks/useExperiment';
import { getDisplayAdAttributes, gptSlotNames } from 'src/common/utils/ads/config';
import { isSmall } from 'src/common/utils/breakpoints';
import { AdSettingsProvider } from '../../contexts/AdSettingsContext';
import { useDetermineAdEligibility } from '../../utils/ads/determineAdEligibility';
import LinkPresentation from './childPresentations/Link';
import NumberedLinkPresentation from './childPresentations/NumberedLink';
import css from './container-items.module.scss';
import Flow from './flow/Flow';
import GalleryLayout from './gallery/Gallery';
import { HeaderlessCard } from './HeaderlessCard';
import ListLayout from './list/ListLayout';
import Matrix from './matrix/Matrix';
import { ProfilePageAds, useCanShowProfilePageAds } from './ProfilePageAds';
import ScheduleCard from './scheduleCard/ScheduleCard';
import LoadMoreItems from './shared/LoadMoreItems';
import Prompt from './shared/Prompt';

const layoutMap = {
  Gallery: GalleryLayout,
  Carousel: GalleryLayout,
  NumberedMatrix: NumberedLinkPresentation,
  LinkMatrix: LinkPresentation,
  Flow,
  Prompt,
  Matrix,
  TileMatrix: Matrix,
  ScheduleCard,

  HeaderlessCard: HeaderlessCard,
  HeaderlessList: HeaderlessCard,
};

function getLayout(layout) {
  return layoutMap[layout] || ListLayout;
}

const ContainerItems = ({
  items,
  isFetching,
  breakpoint,
  routeProps,
  selectedContentId,
  className,
  isDarkMode: isDarkModeProp,
  isNowPlaying,
  isProfilePage,
  onItemClick,
  isInterestSelector,
  disableFirstContainerAd = false,
  guideItem,
  isRightSideOpenOnProfilePage,
}) => {
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isBottomBannerEnabled = useExperiment(BOTTOM_BANNER_ENABLED);
  const canShowAds = useDetermineAdEligibility(guideItem);
  const isDarkMode = !!(isDarkModeProp || routeProps?.isDiscord);
  const canShowProfilePageAds = useCanShowProfilePageAds();
  const matchUrl = routeProps?.matchUrl;
  const shouldShowDisplayAd =
    !isBottomBannerEnabled && !isSmall(breakpoint) && canShowAds && !disableFirstContainerAd;
  const canShowRightSection =
    canShowProfilePageAds && isProfilePage && !isRightSideOpenOnProfilePage;
  let renderedItems;

  if (!items || !items.length) {
    return null;
  }

  const renderItem = (item, i) => {
    const { containerNavigation, guideId, showSeeMore } = item;
    const layout = item.layout;
    const Layout = getLayout(layout);
    const isFirstItem = i === 0;
    const { divider: showDivider, backgroundColor } = item.styles || {};
    const hasTitle = !!item?.title;
    const containerStyles = {
      ...(backgroundColor && { backgroundColor }),
    };

    return (
      <Fragment key={`${selectedContentId}-${i}-${guideId}`}>
        <div
          className={clsx(css.containerItem, {
            [css.profilePage]: isProfilePage,
            [css.adContainer]: item.isAdContainer,
            [css.noSpace]: isInterestSelector,
            [css.reducedMargin]: item.isCardContainer,
            [css.noTopPadding]: !hasTitle,
            [css.firstTitlelessBrowseContainer]: !isProfilePage && !hasTitle && isFirstItem,
          })}
          style={containerStyles}
          data-layout={layout}
        >
          <Layout
            matchUrl={matchUrl}
            containerItem={item}
            breakpoint={breakpoint}
            index={i}
            isDarkMode={isDarkMode}
            isInterestSelector={isInterestSelector}
            isNowPlaying={isNowPlaying}
            isDiscord={routeProps?.isDiscord}
            selectedContentId={selectedContentId}
            id={`container-${i}`}
            onItemClick={onItemClick}
          />
          {showSeeMore && (
            <LoadMoreItems
              containerNavigation={containerNavigation}
              index={i}
              offset={item.children.length}
              selectedContentId={selectedContentId}
              containerItem={item}
            />
          )}
          {showDivider && (
            <div className={css.containerDivider}>
              <hr />
            </div>
          )}
        </div>
        {isFirstItem && shouldShowDisplayAd && (
          <DisplayAd
            style={{ padding: '15px 0' }}
            guideId={routeProps?.guideContext?.guideId}
            matchUrl={matchUrl}
            {...getDisplayAdAttributes(gptSlotNames.browse_mid, isFreestarEnabled)}
          />
        )}
      </Fragment>
    );
  };

  if (canShowRightSection) {
    renderedItems = (
      <>
        {items.slice(0, 1).map(renderItem)}
        <div className={css.sectionsContainer}>
          <div>{items.slice(1).map(renderItem)}</div>
          <div>
            <ProfilePageAds />
          </div>
        </div>
      </>
    );
  } else {
    renderedItems = items.map(renderItem);
  }

  return (
    // data-darkmode attribute is used to apply changes to child components via css
    <div className={className} style={{ opacity: isFetching ? 0.5 : 1 }} data-darkmode={isDarkMode}>
      <AdSettingsProvider isAdEligible={canShowAds} isProfilePage={isProfilePage}>
        {renderedItems}
      </AdSettingsProvider>
    </div>
  );
};

ContainerItems.propTypes = {
  guideItem: PropTypes.object,
  items: PropTypes.array.isRequired,
  itemsStyles: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  breakpoint: PropTypes.number.isRequired,
  selectedContentId: PropTypes.string.isRequired,
  routeProps: PropTypes.object,
  className: PropTypes.string,
  isDarkMode: PropTypes.bool,
  isNowPlaying: PropTypes.bool,
  isProfilePage: PropTypes.bool,
  isInterestSelector: PropTypes.bool,
  onItemClick: PropTypes.func,
  disableFirstContainerAd: PropTypes.bool,
  sponsorshipEnabled: PropTypes.bool,
};

ContainerItems.defaultProps = {
  itemsStyles: {},
  className: '',
  isDarkMode: false,
  isNowPlaying: false,
  isProfilePage: false,
  onItemClick: () => {},
  sponsorshipEnabled: false,
};

export default ContainerItems;
